import { useLazyQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import styled from '@emotion/styled'
import {
  Box,
  Chip as MuiChip,
  Grid2 as Grid,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { green, yellow } from '@mui/material/colors'
import { spacing, SpacingProps } from '@mui/system'
import { isEmpty, round } from 'lodash'
import { useEffect, useState } from 'react'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  DashboardFilter,
  DashboardStatFragment,
  DashboardStatsQuery,
  DashboardStatsQueryVariables,
  GET_DASHBOARD_STATS,
} from '@/graphql'
import useActiveSubscription from '@/hooks/useActiveSubscription'
import { useTablePageState } from '@/hooks/useTablePageState'
import { formatCurrency, formatNumber } from '@/i18n/tolgee'
import useT from '@/i18n/useT'
import {
  getRAGColor,
  MockStore,
  MockStoreStatsOrder,
  mockThresholds,
} from '@/mocks/mockStoresReportTable'

import DeliciousTable, { HeadCell } from '../../components/DeliciousTable'

export interface ChipProps extends SpacingProps {
  status: 'active' | 'inactive'
}

export const Chip = styled(MuiChip)<ChipProps>`
  ${spacing};
  background: ${props =>
    props.status === 'inactive' ? yellow[700] : green[700]};
  color: ${props =>
    props.status === 'active' && props.theme.palette.common.white};
`

function NoDataWithTooltip({ tooltip }: { tooltip: string }) {
  const { t } = useT()
  return (
    <Tooltip title={tooltip}>
      <span>{t('storesReportTable.noData')}</span>
    </Tooltip>
  )
}

// TODO: use real fragment
export type StoreStatsFragment = MockStore
export type StoreStatsOrder = MockStoreStatsOrder

function StoreReportTable({
  showHeader = true,
  filters,
}: {
  showHeader?: boolean
  filters: DashboardFilter
}) {
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()
  const { t } = useT()
  const { isBasic } = useActiveSubscription()

  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState({
    initialRowsPerPage: 5,
  })

  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC')
  const [orderBy, setOrderBy] = useState<keyof StoreStatsOrder>('name')

  const [
    fetchDashboardStats,
    {
      data: storeStatsData,
      loading: storeStatsLoading,
      error: storeStatsError,
      refetch,
      variables,
    },
  ] = useLazyQuery<DashboardStatsQuery, DashboardStatsQueryVariables>(
    GET_DASHBOARD_STATS,
  )

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      fetchDashboardStats({
        variables: {
          filters,
          order: { [orderBy]: order },
          pagination: { limit: rowsPerPage, offset: rowsPerPage * page },
        },
      })
    }
  }, [page, rowsPerPage, orderBy, order, filters])

  const handleRequestSort = (property: keyof StoreStatsOrder) => {
    setPage(0)
    const isAsc = orderBy === property && order === 'ASC'
    setOrder(isAsc ? 'DESC' : 'ASC')
    setOrderBy(property)
  }

  const handleRefresh = () => {
    if (!isEmpty(variables)) {
      refetch()
    }
  }
  useRefresh(handleRefresh)

  const headCells: HeadCell<StoreStatsOrder>[] = [
    {
      id: 'name',
      alignment: 'left' as const,
      label: t('storesReportTable.name'),
      sortKey: 'name',
    },
    ...(isBasic
      ? []
      : [
          {
            id: 'totalRevenue',
            alignment: 'right' as const,
            label: t('storesReportTable.totalRevenue'),
            sortKey: 'totalRevenue' as keyof StoreStatsOrder,
          },
        ]),
    {
      id: 'emptyFacings',
      alignment: 'right' as const,
      label: t('storesReportTable.emptyFacings'),
      sortKey: 'emptyFacings',
    },
    {
      id: 'compliance',
      alignment: 'right' as const,
      label: t('storesReportTable.compliance'),
      sortKey: 'compliance' as keyof StoreStatsOrder,
    },
    {
      id: 'restockCompletion',
      alignment: 'right' as const,
      label: t('storesReportTable.restockCompletion'),
      sortKey: 'restockCompletion',
    },
    {
      id: 'taskCompletion',
      alignment: 'right' as const,
      label: t('storesReportTable.taskCompletion'),
      sortKey: 'taskCompletion',
    },
  ]

  const renderRow = (
    store: DashboardStatFragment,
    index: number,
    onRowClick: (storeId: string) => void,
  ) => {
    return (
      <TableRow
        hover
        role="checkbox"
        tabIndex={-1}
        key={`${store.id}-${index}`}
        onClick={() => onRowClick(store.id)}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell>
          <Box ml={3}>{store.name || store.id}</Box>
        </TableCell>
        {!isBasic && (
          <TableCell align="right">
            <Typography>
              {store.totalRevenue === null ||
              store.totalRevenue === undefined ? (
                <NoDataWithTooltip
                  tooltip={t('storesReportTable.noDataTooltip.totalRevenue')}
                />
              ) : (
                formatCurrency(round(store.totalRevenue))
              )}
            </Typography>
          </TableCell>
        )}
        <TableCell align="right">
          <Typography
            color={getRAGColor(
              mockThresholds.emptyFacings,
              store?.emptyFacings,
              true,
            )}
          >
            {store?.emptyFacings === null ||
            store?.emptyFacings === undefined ? (
              <NoDataWithTooltip
                tooltip={t('storesReportTable.noDataTooltip.emptyFacings')}
              />
            ) : (
              formatNumber(store.emptyFacings)
            )}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            color={getRAGColor(mockThresholds.compliance, store?.compliance)}
          >
            {store?.compliance === null || store?.compliance === undefined ? (
              <NoDataWithTooltip
                tooltip={t('storesReportTable.noDataTooltip.compliance')}
              />
            ) : (
              `${Math.round((store?.compliance || 0) * 100)}%`
            )}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            color={getRAGColor(
              mockThresholds.restockCompletion,
              store?.restockCompletion,
            )}
          >
            {store?.restockCompletion === null ||
            store?.restockCompletion === undefined ? (
              <NoDataWithTooltip
                tooltip={t('storesReportTable.noDataTooltip.restockCompletion')}
              />
            ) : (
              `${Math.round((store?.restockCompletion || 0) * 100)}%`
            )}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            color={getRAGColor(
              mockThresholds.taskCompletion,
              store?.taskCompletion,
            )}
          >
            {store?.taskCompletion === null ||
            store?.taskCompletion === undefined ? (
              <NoDataWithTooltip
                tooltip={t('storesReportTable.noDataTooltip.taskCompletion')}
              />
            ) : (
              `${Math.round((store?.taskCompletion || 0) * 100)}%`
            )}
          </Typography>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      <Stack spacing={4}>
        {showHeader && (
          <Grid justifyContent="space-between" container spacing={10}>
            <Typography variant="h3" gutterBottom display="inline">
              {t('storesReportTable.stores')}
            </Typography>
          </Grid>
        )}
        <Grid container spacing={6}>
          <Grid size={12}>
            <DeliciousTable<DashboardStatFragment, StoreStatsOrder>
              data={storeStatsData?.dashboardStats?.results || []}
              headCells={headCells}
              renderRow={(store, index) =>
                renderRow(store, index, storeId =>
                  handleNavTo(`/stores/${storeId}`),
                )
              }
              order={order === 'ASC' ? 'asc' : 'desc'}
              orderBy={orderBy}
              handleRequestSort={handleRequestSort}
              loading={storeStatsLoading}
              error={storeStatsError?.message}
              totalCount={storeStatsData?.dashboardStats?.totalCount || 0}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              title={t('storesReportTable.stores')}
            />
          </Grid>
        </Grid>
      </Stack>
    </>
  )
}

export default StoreReportTable
